import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { PageShell } from '@/components/layout/page-shell';
import { ArticleRenderer } from '@/components/feature/article-renderer';
import { AudioPlayer } from '@/components/feature/audio-player';
import { VideoEmbedPlayer } from '@/components/feature/video-embed-player';
import { ReactionBar } from '@/components/feature/reaction-bar';
import { RelatedSeeds } from '@/components/feature/related-seeds';
import { useContentDetail, useContentViewer } from '@/hooks/use-content';
import { contentTypeLabels } from '@/types/content';

// SPEC-021: Content viewer page. Mounts the heartbeat loop, dispatches
// to per-type renderers, and tracks scroll progress for articles.

export default function ContentViewerPage() {
  const { id } = useParams<{ id: string }>();
  const { data: item, isLoading } = useContentDetail(id);
  const viewer = useContentViewer(id);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll-based progress for article + mixed content. Audio and video
  // players report their own timeupdate progress via viewer.setProgress
  // through the ArticleRenderer's onProgressEvent callback.
  useEffect(() => {
    if (!item) return;
    if (item.content_type !== 'article' && item.content_type !== 'mixed') return;
    const main = scrollRef.current?.closest('main');
    if (!main) return;

    const onScroll = () => {
      const max = main.scrollHeight - main.clientHeight;
      if (max <= 0) return;
      const percent = (main.scrollTop / max) * 100;
      viewer.setProgress(percent);
      if (percent >= 90) viewer.markComplete();
    };
    main.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed an initial read
    return () => main.removeEventListener('scroll', onScroll);
  }, [item, viewer]);

  if (isLoading || !item) {
    return (
      <PageShell title="Loading…">
        <p className="px-4 py-10 text-center text-sm text-[var(--color-muted)]">
          Loading content…
        </p>
      </PageShell>
    );
  }

  const media = item.media ?? [];
  const primaryAudio = media.find(
    (m) => m.asset_type === 'audio' && m.s3_object_key === item.primary_audio_key,
  );

  return (
    <PageShell title={item.title}>
      <div ref={scrollRef} className="space-y-4 pb-6 pt-2">
        <header className="px-4">
          <p className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
            {contentTypeLabels[item.content_type]}
            {typeof item.estimated_minutes === 'number' &&
              item.estimated_minutes > 0 && (
                <> · {item.estimated_minutes} min</>
              )}
          </p>
          {item.summary && (
            <p className="mt-1 text-sm text-[var(--color-muted)]">{item.summary}</p>
          )}
        </header>

        {item.content_type === 'audio' && primaryAudio?.presigned_url && (
          <div className="px-4">
            <AudioPlayer
              src={primaryAudio.presigned_url}
              title={item.title}
              durationSec={
                item.primary_audio_duration_sec ??
                primaryAudio.duration_sec ??
                undefined
              }
              onProgress={(p) => {
                viewer.setProgress(p);
                if (p >= 90) viewer.markComplete();
              }}
            />
          </div>
        )}

        {item.content_type === 'video' && item.embed_video_id && item.embed_provider && (
          <div className="px-4">
            <VideoEmbedPlayer
              provider={item.embed_provider}
              videoId={item.embed_video_id}
              url={item.embed_url ?? undefined}
              onOpen={() => viewer.recordEvent('open', { provider: item.embed_provider })}
            />
          </div>
        )}

        {(item.content_type === 'article' ||
          item.content_type === 'mixed') && (
          <ArticleRenderer
            doc={item.body}
            media={media}
            onProgressEvent={(type, data) => {
              if (type === 'audio_progress' && typeof data.progress === 'number') {
                viewer.setProgress(data.progress);
                if ((data.progress as number) >= 90) viewer.markComplete();
              }
              if (type === 'video_open') {
                viewer.recordEvent('open', data);
              }
            }}
          />
        )}

        <ReactionBar contentId={item.id} />

        {item.seed_links && item.seed_links.length > 0 && (
          <RelatedSeeds links={item.seed_links} />
        )}
      </div>
    </PageShell>
  );
}
